import type {
  AggregationPeriod,
  ChartCandleData,
  ChartDataOptions,
  ChartDataProvider,
  FullChartDataOptions,
  ServiceData,
} from '../chart/dx-data-provider'
import { translateIntervalForAPI } from '../chart/utils'
import {
  fetchChartFeedHistory,
  openChartFeedStream,
  type ChartFeedStreamConnection,
} from './client'
import type {
  ChartFeedConnectionStatus,
  ChartFeedPriceMessage,
  ChartFeedProvider,
} from './types'

interface CandleSubscription {
  symbol: string
  aggregation: AggregationPeriod
  callback: (data: ChartCandleData[]) => void
  lastCandle?: ChartCandleData
}

interface ChartFeedDataProviderOptions {
  basicToken: string
  provider: ChartFeedProvider
  onStatusChange?: (status: ChartFeedConnectionStatus) => void
  onTick?: (tick: ChartFeedPriceMessage) => void
  onStreamError?: (message: string) => void
}

function normalizeSymbol(symbol: string): string {
  return symbol.trim().toUpperCase()
}

function intervalToBucketStart(timestampMs: number, aggregation: AggregationPeriod): number {
  const interval = translateIntervalForAPI(aggregation)

  const MILLIS = {
    minute: 60_000,
    hour: 3_600_000,
    day: 86_400_000,
    week: 7 * 86_400_000,
  }

  const toFloor = (sizeMs: number): number => Math.floor(timestampMs / sizeMs) * sizeMs

  switch (interval) {
    case '1m':
      return toFloor(MILLIS.minute)
    case '5m':
      return toFloor(5 * MILLIS.minute)
    case '15m':
      return toFloor(15 * MILLIS.minute)
    case '30m':
      return toFloor(30 * MILLIS.minute)
    case '1h':
      return toFloor(MILLIS.hour)
    case '2h':
      return toFloor(2 * MILLIS.hour)
    case '4h':
      return toFloor(4 * MILLIS.hour)
    case '1d':
      return toFloor(MILLIS.day)
    case '1w':
      return toFloor(MILLIS.week)
    case '1mo': {
      const date = new Date(timestampMs)
      return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0)
    }
    default:
      return toFloor(MILLIS.minute)
  }
}

function createNextCandle(
  lastCandle: ChartCandleData | undefined,
  price: number,
  timestampMs: number,
  aggregation: AggregationPeriod,
): ChartCandleData | undefined {
  const bucketStart = intervalToBucketStart(timestampMs, aggregation)

  if (!lastCandle) {
    return {
      time: bucketStart,
      open: price,
      high: price,
      low: price,
      close: price,
      volume: 0,
    }
  }

  if (bucketStart < lastCandle.time) {
    return undefined
  }

  if (bucketStart === lastCandle.time) {
    return {
      ...lastCandle,
      high: Math.max(lastCandle.high, price),
      low: Math.min(lastCandle.low, price),
      close: price,
    }
  }

  return {
    time: bucketStart,
    open: lastCandle.close,
    high: Math.max(lastCandle.close, price),
    low: Math.min(lastCandle.close, price),
    close: price,
    volume: 0,
  }
}

export class ChartFeedDataProvider implements ChartDataProvider {
  private candleSubscribers = new Map<string, CandleSubscription>()
  private serviceDataSubscribers = new Map<string, (data: ServiceData) => void>()
  private streamConnection?: ChartFeedStreamConnection
  private streamSymbol: string | null = null
  private disconnected = false

  constructor(private readonly options: ChartFeedDataProviderOptions) {}

  private toIsoTimestamp(epochValue?: number): string | undefined {
    if (typeof epochValue !== 'number' || !Number.isFinite(epochValue)) {
      return undefined
    }

    const millis = epochValue > 1e12 ? epochValue : epochValue * 1000
    return new Date(millis).toISOString()
  }

  async requestHistoryData(
    symbol: string,
    aggregation: AggregationPeriod,
    options?: FullChartDataOptions,
    updateCallback?: (data: ChartCandleData[]) => void,
    _chartId?: string,
  ): Promise<ChartCandleData[]> {
    const interval = translateIntervalForAPI(aggregation)

    const outputsize = (() => {
      if (options?.fromTime && options?.toTime && options.toTime > options.fromTime) {
        const windowMs = (options.toTime - options.fromTime) * (options.toTime > 1e12 ? 1 : 1000)
        const oneMinuteMs = 60_000
        return Math.min(Math.max(Math.ceil(windowMs / oneMinuteMs), 300), 5000)
      }
      return 300
    })()

    try {
      const history = await fetchChartFeedHistory({
        basicToken: this.options.basicToken,
        provider: this.options.provider,
        symbol,
        interval,
        outputsize,
        startDate: this.toIsoTimestamp(options?.fromTime),
        endDate: this.toIsoTimestamp(options?.toTime),
        order: 'asc',
      })

      const candles = history.data
        .map((entry) => {
          const timeMs = Date.parse(entry.timestamp)
          if (!Number.isFinite(timeMs)) {
            return null
          }

          return {
            time: timeMs,
            open: entry.open,
            high: entry.high,
            low: entry.low,
            close: entry.close,
            volume: typeof entry.volume === 'number' && Number.isFinite(entry.volume) ? entry.volume : 0,
          } satisfies ChartCandleData
        })
        .filter((entry): entry is ChartCandleData => entry !== null)

      if (candles.length === 0) {
        const message = 'No historical candles available for the selected symbol and range.'
        this.options.onStreamError?.(message)
        throw new Error(message)
      }

      updateCallback?.(candles)
      return candles
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load historical candles.'
      this.options.onStreamError?.(message)
      throw error instanceof Error ? error : new Error(message)
    }
  }

  subscribeCandles(
    symbol: string,
    aggregation: AggregationPeriod,
    subscriptionId: string,
    subscribeCallback: (data: ChartCandleData[]) => void,
    _options?: ChartDataOptions,
    _chartId?: string,
  ): void {
    this.candleSubscribers.set(subscriptionId, {
      symbol,
      aggregation,
      callback: subscribeCallback,
    })
    this.ensureStreamConnection(symbol)
  }

  unsubscribeCandles(subscriptionId: string): void {
    this.candleSubscribers.delete(subscriptionId)
    this.maybeDisconnectStream()
  }

  subscribeServiceData(symbol: string, subscribeCallback: (data: ServiceData) => void): void {
    this.serviceDataSubscribers.set(symbol, subscribeCallback)
    this.ensureStreamConnection(symbol)
  }

  unsubscribeServiceData(symbol: string): void {
    this.serviceDataSubscribers.delete(symbol)
    this.maybeDisconnectStream()
  }

  disconnect(): void {
    this.disconnected = true
    this.streamConnection?.close()
    this.streamConnection = undefined
    this.streamSymbol = null
    this.candleSubscribers.clear()
    this.serviceDataSubscribers.clear()
  }

  refreshChart(): void {
    // No-op for chart feed landing page provider.
  }

  private resolveDesiredSymbol(preferredSymbol?: string): string | null {
    if (preferredSymbol && preferredSymbol.trim()) {
      return preferredSymbol
    }

    for (const subscriber of this.candleSubscribers.values()) {
      if (subscriber.symbol.trim()) {
        return subscriber.symbol
      }
    }

    for (const symbol of this.serviceDataSubscribers.keys()) {
      if (symbol.trim()) {
        return symbol
      }
    }

    return null
  }

  private ensureStreamConnection(preferredSymbol?: string): void {
    if (this.disconnected) {
      return
    }

    const desiredSymbol = this.resolveDesiredSymbol(preferredSymbol)
    if (!desiredSymbol) {
      this.streamConnection?.close()
      this.streamConnection = undefined
      this.streamSymbol = null
      return
    }

    if (
      this.streamConnection &&
      this.streamSymbol &&
      normalizeSymbol(this.streamSymbol) === normalizeSymbol(desiredSymbol)
    ) {
      return
    }

    this.streamConnection?.close()
    this.streamConnection = undefined
    this.streamSymbol = desiredSymbol

    this.streamConnection = openChartFeedStream({
      basicToken: this.options.basicToken,
      provider: this.options.provider,
      symbol: desiredSymbol,
      onStatusChange: (status) => {
        this.options.onStatusChange?.(status)
      },
      onErrorMessage: (message) => {
        this.options.onStreamError?.(message)
      },
      onPriceMessage: (message) => {
        this.handlePriceMessage(message)
      },
    })
  }

  private maybeDisconnectStream(): void {
    if (this.candleSubscribers.size > 0 || this.serviceDataSubscribers.size > 0) {
      this.ensureStreamConnection()
      return
    }

    this.streamConnection?.close()
    this.streamConnection = undefined
    this.streamSymbol = null
  }

  private handlePriceMessage(message: ChartFeedPriceMessage): void {
    const actualSymbol = normalizeSymbol(message.logicalSymbol)

    if (this.streamSymbol && normalizeSymbol(this.streamSymbol) !== actualSymbol) {
      return
    }

    this.options.onTick?.(message)

    const timestampMs = Date.parse(message.receivedAt)
    if (!Number.isFinite(timestampMs)) {
      return
    }

    for (const subscriber of this.candleSubscribers.values()) {
      if (normalizeSymbol(subscriber.symbol) !== actualSymbol) {
        continue
      }

      const nextCandle = createNextCandle(
        subscriber.lastCandle,
        message.price,
        timestampMs,
        subscriber.aggregation,
      )

      if (!nextCandle) {
        continue
      }

      subscriber.lastCandle = nextCandle
      subscriber.callback([nextCandle])
    }

    const bid = Number.isFinite(message.bid) ? message.bid : message.price
    const ask = Number.isFinite(message.ask) ? message.ask : message.price

    for (const [symbol, callback] of this.serviceDataSubscribers.entries()) {
      if (normalizeSymbol(symbol) !== actualSymbol) {
        continue
      }

      callback({ bid, ask })
    }
  }
}
